import dayjs, { type Dayjs } from "dayjs";

import {
  normalizeHelpSearch,
  type HelpArticleEntity,
  type HelpArticleKind,
  type HelpCategory,
  type HelpChangeType,
  type HelpPlatform,
  type HelpRelease,
} from "@fitness-app/data-models/entities/HelpArticle";
import { type HelpContent } from "@fitness-app/data-models/entities/HelpContent";
import { type Database, type Json } from "@fitness-app/supabase";

export type HelpArticleRowInsert = Database["public"]["Tables"]["help_article"]["Insert"];

export interface HelpArticleFormValue {
  kind: HelpArticleKind;
  slug: string;
  title: string;
  summary: string;
  availability: string;
  category: HelpCategory;
  platforms: HelpPlatform[];
  keywords: string[];
  related: string[];
  changeType: HelpChangeType | null;
  /** Data wydania funkcji — osobna od daty publikacji materiału. */
  releasedAt: Dayjs | null;
  releases: HelpRelease[];
}

export const emptyHelpArticleForm = (kind: HelpArticleKind): HelpArticleFormValue => ({
  kind,
  slug: "",
  title: "",
  summary: "",
  availability: "",
  category: "start",
  platforms: ["mobile"],
  keywords: [],
  related: [],
  changeType: kind === "update" ? "new" : null,
  releasedAt: kind === "update" ? dayjs() : null,
  releases: [],
});

export const toHelpArticleForm = (article: HelpArticleEntity): HelpArticleFormValue => ({
  kind: article.kind as HelpArticleKind,
  slug: article.slug,
  title: article.title,
  summary: article.summary,
  availability: article.availability,
  category: article.category as HelpCategory,
  platforms: article.platforms as HelpPlatform[],
  keywords: article.keywords,
  related: article.related,
  changeType: (article.changeType as HelpChangeType | null) ?? null,
  releasedAt: article.releasedAt ? dayjs(article.releasedAt) : null,
  releases: (article.releases as HelpRelease[] | null) ?? [],
});

// Pole, którego formularz nie renderuje dla danego rodzaju materiału, wraca
// jako undefined — mapowanie nie może się o to wywrócić.
const cleanList = (values: string[] | undefined) => (values ?? []).map((value) => value.trim()).filter(Boolean);

/**
 * Wartość formularza → wiersz `help_article`.
 *
 * Status i `publishedAt` świadomie NIE wchodzą: zmienia je osobna akcja
 * publikacji, żeby zapis literówki w opublikowanym materiale nie przesuwał daty
 * publikacji ani nie wymagał klucza `helpCenter.publish`.
 */
export const toHelpArticleRow = (
  value: HelpArticleFormValue,
  { userId, isNew, content }: { userId: string | null; isNew: boolean; content?: HelpContent },
): HelpArticleRowInsert => ({
  kind: value.kind,
  slug: value.slug.trim(),
  title: value.title.trim(),
  summary: value.summary.trim(),
  availability: (value.availability ?? "").trim(),
  category: value.category,
  platforms: value.platforms,
  keywords: cleanList(value.keywords),
  related: cleanList(value.related),
  // Instrukcja nigdy nie niesie rodzaju zmiany — baza pilnuje tego checkiem,
  // a formularz potrafi zostawić wartość po przełączeniu rodzaju materiału.
  changeType: value.kind === "update" ? value.changeType : null,
  releasedAt: value.kind === "update" && value.releasedAt ? value.releasedAt.format("YYYY-MM-DD") : null,
  releases: value.releases?.length ? (value.releases as unknown as Json) : null,
  updatedBy: userId,
  ...(content ? { content: content as unknown as Json } : {}),
  ...(isNew ? { createdBy: userId } : {}),
});

// Przycięcie idzie PRZED obcięciem myślników: tytuł dłuższy niż 80 znaków
// potrafi zostać ucięty na separatorze, a slug kończący się myślnikiem odbija
// i schema, i CHECK w bazie (`^[a-z0-9]+(-[a-z0-9]+)*$`).
export const slugifyHelpTitle = (title: string): string =>
  normalizeHelpSearch(title)
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 80)
    .replace(/^-+|-+$/g, "");

/**
 * Kolejność instrukcji na /pomoc: ręczna pozycja, a materiały bez numeru na
 * koniec. Ten sam porządek liczy client-web, więc przeciągnięcie w panelu ma
 * odbić się na liście klientki jeden do jednego.
 */
export const byGuideOrder = (first: HelpArticleEntity, second: HelpArticleEntity): number => {
  const firstPosition = first.position ?? Number.MAX_SAFE_INTEGER;
  const secondPosition = second.position ?? Number.MAX_SAFE_INTEGER;
  if (firstPosition !== secondPosition) {
    return firstPosition - secondPosition;
  }
  return first.title.localeCompare(second.title, "pl");
};

/** Archiwum „Co nowego” idzie po dacie wydania funkcji, nie po dacie redakcji. */
export const byReleaseDate = (first: HelpArticleEntity, second: HelpArticleEntity): number =>
  (second.releasedAt ?? second.updatedAt).localeCompare(first.releasedAt ?? first.updatedAt);
